import { trackEcommerce, EcomProduct, trackingConstants, eventNameConstants } from 'dibs-tracking';
import { LOCALE_US } from 'dibs-intl/exports/locales';
import { pageTypeConstants as pageTypes } from '../../../constants/pageTypeConstants';
import { ECOMMERCE_ATTRIBUTES } from '../../../constants/attributesConstants';
import { PAGE_TYPE } from 'dibs-constants/exports/pageTypes';
import { getPriceType } from '../shared/displayPriceTracking';

import {
    getIsFreeShipping,
    getLowestEligibleShippingCost,
    UserRegions,
} from 'dibs-search-product-tile/exports/shippingCostHelpers';

const LIST_TRACKING_SEARCH = 'search results listing';
const LIST_TRACKING_BROWSE = 'browse page listing';
const LIST_TRACKING_ATTRIBUTE = 'attribute listing';
const LIST_TRACKING_STOREFRONT = 'storefront listing';
const LIST_TRACKING_BUY_PAGE = 'buy page';
const LIST_TRACKING_SEMANTIC = 'semantic result listing';
const LIST_TRACKING_RELATED_ITEMS = 'null results - related items';

import { ArrayElement } from 'dibs-ts-utils/exports/ArrayElement';

import { SbSharedThinBrowseItemTrackingProvider_items$data as ThinBrowseProviderItems } from '../../../finding/SbSharedThinBrowseItemTrackingProvider/__generated__/SbSharedThinBrowseItemTrackingProvider_items.graphql';

import SV from 'server-vars';

export const NUMBER_OF_PRODUCT_IMPRESSIONS = 20;

type PageTypesConstantType = typeof pageTypes;
type PageType = PageTypesConstantType[keyof PageTypesConstantType];
type SearchTermType = string | null;
type ItemsType = ThinBrowseProviderItems;
type ItemType = ArrayElement<ItemsType>;
type WrappedItemType = { item: ItemType };

export const getListType = (
    pageType: PageType,
    searchCorrectionTerm: string,
    isRelatedSearchItems?: boolean
): string => {
    const { SEARCH, BROWSE, DEALER } = pageTypes;

    if (searchCorrectionTerm && pageType !== SEARCH) {
        return LIST_TRACKING_SEMANTIC;
    } else if (isRelatedSearchItems) {
        return LIST_TRACKING_RELATED_ITEMS;
    } else if (pageType === SEARCH) {
        return LIST_TRACKING_SEARCH;
    } else if (pageType === BROWSE) {
        return LIST_TRACKING_BROWSE;
    } else if (ECOMMERCE_ATTRIBUTES.includes(pageType)) {
        return LIST_TRACKING_ATTRIBUTE;
    } else if (pageType === DEALER) {
        return LIST_TRACKING_STOREFRONT;
    } else if (pageType === PAGE_TYPE.BUY) {
        return LIST_TRACKING_BUY_PAGE;
    } else {
        return `${pageType} listing`;
    }
};

type ProductItem = {
    readonly serviceId: string | null;
    readonly title: string | null;
    readonly seller: {
        readonly serviceId: string | null;
    } | null;
    readonly displayPriceTracking: ReadonlyArray<{
        readonly convertedAmountList: ReadonlyArray<{
            readonly amount: number | null;
            readonly currency: string | null;
        } | null> | null;
        readonly amountType: string | null;
        readonly textType: string | null;
    } | null> | null;
    readonly categoryCode: string | null;
    readonly browseUrl: string | null;
    readonly contemporaryTrackingString: string | null;
    readonly isAuctionEnabled: boolean | null;
};

export const getProductData = ({
    index,
    item,
    pageType,
    searchTerm,
    searchCorrectionTerm = '',
    /**
     * Ideally we would avoid using the global version of SV, keeping this for legacy code.
     */
    locale = SV.get('locale'),
    isRelatedSearchItems,
}: {
    index: number;
    item: ProductItem;
    pageType: PageType;
    searchTerm: SearchTermType;
    searchCorrectionTerm?: string;
    locale?: string;
    isRelatedSearchItems?: boolean;
}): EcomProduct => {
    const {
        serviceId,
        title,
        seller,
        displayPriceTracking,
        categoryCode,
        browseUrl,
        contemporaryTrackingString,
        isAuctionEnabled,
    } = item;

    const primaryDiplayPriceTracking = displayPriceTracking?.[0];
    const convertedAmountList = primaryDiplayPriceTracking?.convertedAmountList;
    const amountType = primaryDiplayPriceTracking?.amountType;
    const textType = primaryDiplayPriceTracking?.textType;
    const priceType = getPriceType({ isAuctionEnabled, amountType, textType });

    const usdPrice = (convertedAmountList || []).find(
        convertedAmount => convertedAmount?.currency === 'USD'
    );
    const price = usdPrice?.amount || 0;

    const baseObject = {
        id: serviceId,
        name: locale === LOCALE_US ? title : 'n/a',
        position: index + 1, // 1-indexed
        brand: seller?.serviceId,
        category: browseUrl,
        categoryCode,
        dimension76: priceType,
        dimension83: contemporaryTrackingString,
        price,
        list: getListType(pageType, searchCorrectionTerm, isRelatedSearchItems),
    };

    return Object.assign(
        baseObject,
        (searchTerm && pageType === pageTypes.SEARCH) || searchCorrectionTerm
            ? {
                  dimension98: searchCorrectionTerm || searchTerm,
                  searchCategoryClassification:
                      pageType === pageTypes.SEARCH && searchCorrectionTerm ? 'Y' : 'N',
              }
            : {}
    );
};

function getUserProdutData({ item, userRegions }: { item: ItemType; userRegions: UserRegions }): {
    dimension106: string;
    dimension120: number | undefined;
} {
    return {
        dimension106: getIsFreeShipping({ item, userRegions }) ? 'Y' : 'N',
        dimension120: getLowestEligibleShippingCost({ item, userRegions }),
    };
}

const { ECOM_PRODUCT_CLICK, ECOM_PRODUCT_IMPRESSION } = trackingConstants;
const { EVENT_SELECT_ITEM, EVENT_VIEW_ITEM_LIST } = eventNameConstants;

export const trackEcommerceProductImpressions = ({
    items,
    pageType,
    searchTerm,
    startIndex = 0,
    searchCorrectionTerm,
    userRegions,
    isRelatedSearchItems,
}: {
    items: { node: ItemType | WrappedItemType }[];
    pageType: PageType;
    searchTerm: SearchTermType;
    startIndex: number;
    searchCorrectionTerm?: string;
    userRegions: UserRegions;
    isRelatedSearchItems?: boolean;
}): void => {
    const itemProductData = items
        .slice(startIndex, startIndex + NUMBER_OF_PRODUCT_IMPRESSIONS)
        .map(({ node }, index) => ({
            ...getUserProdutData({ item: (node as WrappedItemType)?.item || node, userRegions }),
            ...getProductData({
                pageType,
                searchTerm,
                searchCorrectionTerm,
                item: (node as WrappedItemType)?.item || node,
                index: startIndex + index,
                isRelatedSearchItems,
            }),
        }));

    trackEcommerce({
        type: ECOM_PRODUCT_IMPRESSION,
        eventName: EVENT_VIEW_ITEM_LIST,
        products: itemProductData,
    });
};

export const trackEcommerceProductClick = ({
    item,
    index,
    pageType,
    searchTerm,
    searchCorrectionTerm = '',
    userRegions,
}: {
    index: number;
    item: ItemType;
    pageType: string;
    searchTerm: SearchTermType;
    searchCorrectionTerm?: string;
    userRegions: UserRegions;
}): void => {
    trackEcommerce({
        type: ECOM_PRODUCT_CLICK,
        eventName: EVENT_SELECT_ITEM,
        actionField: {
            list: getListType(pageType, searchCorrectionTerm),
            itemId: item?.serviceId,
        },
        products: [
            {
                ...getProductData({
                    item,
                    pageType,
                    index,
                    searchTerm,
                    searchCorrectionTerm,
                }),
                ...getUserProdutData({
                    item,
                    userRegions,
                }),
            },
        ],
    });
};

// TODO: FINDING-12480 make sure itemFragment below contains all
// information for every function that uses item in this file and
// remove fields from individual components

// export const itemFragment = graphql`
//     fragment ecommerceTracking_item on Item {
//         title
//         serviceId
//         categoryPath
//         contemporaryTrackingString
//         vertical
//         seller {
//             serviceId
//         }
//         displayInfo(buyerId: $userId) @include(if: $isClient) {
//             pricing {
//                 showNet
//                 showSale
//             }
//         }
//         pricing {
//             retailPrice {
//                 amount
//             }
//             netPrice {
//                 amount
//             }
//             salePrice {
//                 amount
//             }
//         }
//         shipmentQuotes {
//             totalAmount {
//                 amount
//             }
//             serviceMethod {
//                 region {
//                     regionName
//                 }
//             }
//         }
//     }
// `;
